"use client";

import {
  LANGUAGE_OPTIONS,
  type DiagnosticTraceStep,
  type SupportedLanguageCode,
} from "@fluentry/domain";
import { Badge, Button } from "@fluentry/ui";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Download,
  FileText,
  ListChecks,
  Sparkles,
  Target,
} from "lucide-react";
import { useState } from "react";

type ConvertResultSummaryProps = {
  sourceInput?: string;
  interpretedIntent?: string;
  targetOutput?: string;
  diagnosticTrace?: DiagnosticTraceStep[];
  confidence?: number;
  resolvedTargetLanguage?: SupportedLanguageCode;
  onUseAsDraft?: (targetOutput: string) => void;
};

const languageLabel = (code: SupportedLanguageCode | undefined) =>
  LANGUAGE_OPTIONS.find((option) => option.value === code)?.label ?? code;

export async function writeClipboardText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "fixed";
  textArea.style.top = "-1000px";
  textArea.style.left = "-1000px";
  document.body.appendChild(textArea);
  textArea.select();
  try {
    if (!document.execCommand("copy")) {
      throw new Error("Clipboard copy command was rejected");
    }
  } finally {
    textArea.remove();
  }
}

export function buildResultExportText({
  sourceInput,
  interpretedIntent,
  targetOutput,
  diagnosticTrace,
  resolvedTargetLanguage,
}: {
  sourceInput?: string;
  interpretedIntent?: string;
  targetOutput: string;
  diagnosticTrace?: DiagnosticTraceStep[];
  resolvedTargetLanguage?: SupportedLanguageCode;
}) {
  return [
    "Fluentry result",
    resolvedTargetLanguage ? `Output language: ${languageLabel(resolvedTargetLanguage)}` : null,
    sourceInput ? `Original draft: ${sourceInput}` : null,
    interpretedIntent ? `What Fluentry understood: ${interpretedIntent}` : null,
    "",
    `Natural result: ${targetOutput}`,
    Array.isArray(diagnosticTrace) && diagnosticTrace.length > 0 ? "" : null,
    Array.isArray(diagnosticTrace) && diagnosticTrace.length > 0 ? "How it got there:" : null,
    ...(diagnosticTrace?.map((step, index) => `${index + 1}. ${step.label}: ${step.detail}`) ?? []),
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

function buildResultFileName(createdAt = new Date()) {
  const timestamp = createdAt
    .toISOString()
    .replace(/\.\d{3}Z$/, "Z")
    .replaceAll(":", "-");
  return `fluentry-result-${timestamp}.txt`;
}

export function downloadResultText(args: {
  sourceInput?: string;
  interpretedIntent?: string;
  targetOutput: string;
  diagnosticTrace?: DiagnosticTraceStep[];
  resolvedTargetLanguage?: SupportedLanguageCode;
}) {
  if (typeof URL.createObjectURL !== "function") {
    throw new Error("File download is not available in this browser");
  }

  const blob = new Blob([buildResultExportText(args)], {
    type: "text/plain;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = buildResultFileName();
  link.rel = "noopener";
  document.body.appendChild(link);
  try {
    // The generated result often needs to leave Fluentry for docs/mail/chat, so provide a file path when clipboard is unavailable.
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(url);
  }
}

export function ConvertResultSummary({
  sourceInput,
  interpretedIntent,
  targetOutput,
  diagnosticTrace,
  confidence,
  resolvedTargetLanguage,
  onUseAsDraft,
}: ConvertResultSummaryProps) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [briefCopyState, setBriefCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [downloadState, setDownloadState] = useState<"idle" | "downloaded" | "error">("idle");
  const hasTrace = Array.isArray(diagnosticTrace) && diagnosticTrace.length > 0;
  // The summary stays hidden until the engine returns something the reader could not infer from the editor alone.
  if (!interpretedIntent && !targetOutput && !hasTrace) {
    return null;
  }

  const confidencePercent =
    typeof confidence === "number" ? Math.round(Math.max(0, Math.min(1, confidence)) * 100) : null;

  async function copyTargetOutput() {
    if (!targetOutput) {
      return;
    }

    try {
      // Copy is an explicit user action because the generated sentence usually leaves Fluentry for mail, chat, or docs.
      await writeClipboardText(targetOutput);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 2_000);
    } catch {
      setCopyState("error");
    }
  }

  async function copyResultBrief() {
    if (!targetOutput) {
      return;
    }

    try {
      // Brief copy preserves the context needed when the result moves into mail, chat, notes, or a support handoff.
      await writeClipboardText(
        buildResultExportText({
          sourceInput,
          interpretedIntent,
          targetOutput,
          diagnosticTrace,
          resolvedTargetLanguage,
        }),
      );
      setBriefCopyState("copied");
      window.setTimeout(() => setBriefCopyState("idle"), 2_000);
    } catch {
      setBriefCopyState("error");
    }
  }

  function downloadResult() {
    if (!targetOutput) {
      return;
    }

    try {
      // Download is the durable escape hatch for browsers or contexts where clipboard handoff is unavailable.
      downloadResultText({
        sourceInput,
        interpretedIntent,
        targetOutput,
        diagnosticTrace,
        resolvedTargetLanguage,
      });
      setDownloadState("downloaded");
      window.setTimeout(() => setDownloadState("idle"), 2_000);
    } catch {
      setDownloadState("error");
    }
  }

  return (
    <section
      aria-label="Inference result"
      className="grid gap-4 rounded-[1.45rem] border border-[#0d8b87]/15 bg-[#f7faf9] p-4 shadow-sm shadow-[#aabfc0]/18 sm:p-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="grid gap-1">
          <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.24em] text-[#5f7c80] uppercase">
            <Sparkles className="h-3.5 w-3.5 text-[#0b6d70]" />
            Latest result
          </div>
          <h2 className="m-0 font-serif text-xl tracking-tight text-slate-950">
            Keep the result in view before you archive it
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {targetOutput ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-11 rounded-full border-[#0f766e]/10 bg-white/80 px-4 text-sm font-semibold text-[#0b6d70] shadow-none hover:bg-white"
              onClick={() => {
                void copyResultBrief();
              }}
            >
              {briefCopyState === "copied" ? (
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              ) : briefCopyState === "error" ? (
                <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Copy className="mr-1.5 h-3.5 w-3.5" />
              )}
              {briefCopyState === "copied"
                ? "Brief copied"
                : briefCopyState === "error"
                  ? "Brief copy failed"
                  : "Copy brief"}
            </Button>
          ) : null}
          {confidencePercent !== null ? (
            <Badge
              variant="outline"
              className="min-h-9 rounded-full border-[#0f766e]/10 bg-[#f1f7f5] px-3 text-[10px] font-semibold tracking-[0.2em] text-[#0b6d70] uppercase shadow-none"
            >
              {confidencePercent}% confidence
            </Badge>
          ) : null}
        </div>
      </div>

      {interpretedIntent ? (
        <div className="grid gap-1 rounded-[1.05rem] border border-[#0f766e]/10 bg-white/72 px-4 py-3">
          <span className="text-[11px] font-semibold tracking-[0.12em] text-[#5f7c80] uppercase">
            What Fluentry understood
          </span>
          <p className="m-0 text-sm leading-relaxed text-slate-700">{interpretedIntent}</p>
        </div>
      ) : null}

      {targetOutput ? (
        <div className="grid gap-3 rounded-[1.15rem] border border-[#0d8b87]/15 bg-white px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] text-[#0b6d70] uppercase">
              <Target className="h-3.5 w-3.5" />
              Natural result
              {resolvedTargetLanguage ? ` · ${languageLabel(resolvedTargetLanguage)}` : ""}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
              <Button
                type="button"
                variant="outline"
                className="min-h-11 rounded-[0.95rem] border-[#0f766e]/10 bg-[#f7faf9] px-3 text-sm font-semibold text-[#0b6d70] shadow-none hover:bg-white"
                onClick={() => onUseAsDraft?.(targetOutput)}
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" />
                Use as next draft
              </Button>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 rounded-[0.95rem] border-[#0f766e]/10 bg-[#f7faf9] px-3 text-sm font-semibold text-[#0b6d70] shadow-none hover:bg-white"
                onClick={() => {
                  void copyTargetOutput();
                }}
              >
                {copyState === "copied" ? (
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                ) : copyState === "error" ? (
                  <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
                ) : (
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                )}
                {copyState === "copied"
                  ? "Copied"
                  : copyState === "error"
                    ? "Copy failed"
                    : "Copy result"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="col-span-2 min-h-11 rounded-[0.95rem] border-[#0f766e]/10 bg-[#f7faf9] px-3 text-sm font-semibold text-[#0b6d70] shadow-none hover:bg-white sm:col-span-1"
                onClick={downloadResult}
              >
                {downloadState === "downloaded" ? (
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                ) : downloadState === "error" ? (
                  <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
                ) : (
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                )}
                {downloadState === "downloaded"
                  ? "Downloaded"
                  : downloadState === "error"
                    ? "Download failed"
                    : "Download"}
              </Button>
            </div>
          </div>
          <p className="m-0 text-base leading-relaxed font-medium text-slate-950">{targetOutput}</p>
        </div>
      ) : null}

      {hasTrace ? (
        <div className="grid gap-2">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] text-[#5f7c80] uppercase">
            <ListChecks className="h-3.5 w-3.5 text-[#0b6d70]" />
            How it got there
          </span>
          <ol className="m-0 grid list-none gap-2 p-0">
            {diagnosticTrace?.map((step, index) => (
              <li
                key={`${step.label}-${index}`}
                className="flex gap-3 rounded-[0.95rem] bg-white/72 px-3 py-3"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f1f7f5] text-[11px] font-semibold text-[#0b6d70]">
                  {index + 1}
                </span>
                <div className="grid gap-0.5">
                  <span className="text-[13px] font-semibold text-slate-800">{step.label}</span>
                  <span className="text-[13px] leading-relaxed text-[#5f7c80]">{step.detail}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
